import type {
  ManagedAdmin,
  ManagedDomain,
  ManagedRole,
  ManagedUser,
} from "../../types";

export type TabKey = "users" | "admins" | "domains" | "roles" | "trash";
export type ModalMode = Exclude<TabKey, "trash">;
export type FormState = Record<string, any>;
export type SelectedRecord = ManagedUser | ManagedAdmin | ManagedDomain | ManagedRole | null;
export type ListItem = ManagedUser | ManagedAdmin | ManagedDomain | ManagedRole;
export type TrashRow =
  | (ManagedUser & { type: "users" })
  | (ManagedAdmin & { type: "admins" })
  | (ManagedDomain & { type: "domains" })
  | (ManagedRole & { type: "roles" });

export const PAGE_SIZE = 10;


export const DEFAULT_FORM_STATE: Record<ModalMode, FormState> = {
  users: { name: "", login: "", email: "", phone: "", password: "", systemIds: [] },
  admins: {
    name: "",
    login: "",
    email: "",
    phone: "",
    password: "",
    systemIds: [],
    roleId: undefined,
    level: 5,
    readonly: false,
  },
  domains: { code: "", name: "", description: "", url: "", allowedRedirectOrigins: [], icon: "", isActive: true },
  roles: { name: "", level: 5 },
};

export const groupedRolePermissions = [
  {
    groupName: "Questions",
    subgroups: [
      {
        subgroupName: "Subject Topic",
        rolePermissions: [
          { label: "Subject Topic", value: "subjectTopic" },
          { label: "Subject Topic Create", value: "subjectTopicCreate" },
          { label: "Subject Topic Update", value: "subjectTopicUpdate" },
          { label: "Subject Topic Delete", value: "subjectTopicDelete" },
        ],
      },
      {
        subgroupName: "Questions",
        rolePermissions: [
          { label: "Question", value: "question" },
          { label: "Question Create", value: "questionCreate" },
          { label: "Question Update", value: "questionUpdate" },
          { label: "Question Delete", value: "questionDelete" },
          { label: "Question Verify", value: "questionReview" },
          { label: "Question Access All", value: "questionAccessAll" },
          { label: "Question Generate Explanation", value: "questionGenerateExplanation" },
          { label: "Integration Client", value: "integrationClient" },
        ],
      },
      {
        subgroupName: "Template",
        rolePermissions: [
          { label: "Template", value: "clipboards" },
          { label: "Template Access All", value: "clipboardsAll" },
          { label: "Template Create", value: "clipboardsCreate" },
          { label: "Template Update", value: "clipboardsUpdate" },
          { label: "Template Delete", value: "clipboardsDelete" },
          { label: "Template Transfer", value: "clipboardsTransfer" },
        ],
      },
      {
        subgroupName: "Source",
        rolePermissions: [
          { label: "Source", value: "source" },
          { label: "Source Create", value: "sourceCreate" },
          { label: "Source Update", value: "sourceUpdate" },
          { label: "Source Delete", value: "sourceDelete" },
        ],
      },
      {
        subgroupName: "Topic",
        rolePermissions: [
          { label: "Topic", value: "topic" },
          { label: "Topic Create", value: "topicCreate" },
          { label: "Topic Update", value: "topicUpdate" },
          { label: "Topic Delete", value: "topicDelete" },
        ],
      },
    ],
  },
  {
    groupName: "Section",
    isNotNested: true,
    subgroups: [
      {
        subgroupName: "Section",
        rolePermissions: [
          { label: "Section", value: "section" },
          { label: "Section Create", value: "sectionCreate" },
          { label: "Section Update", value: "sectionUpdate" },
          { label: "Section Delete", value: "sectionDelete" },
          { label: "Section Access All", value: "sectionAccessAll" },
        ],
      },
    ],
  },
  {
    groupName: "Course",
    isNotNested: true,
    subgroups: [
      {
        subgroupName: "Course",
        rolePermissions: [
          { label: "Course", value: "course" },
          { label: "Course Create", value: "courseCreate" },
          { label: "Course Update", value: "courseUpdate" },
          { label: "Course Delete", value: "courseDelete" },
        ],
      },
    ],
  },
  {
    groupName: "Practice",
    isNotNested: true,
    subgroups: [
      {
        subgroupName: "Overall",
        rolePermissions: [
          { label: "Practice", value: "practice" },
          { label: "Practice Create", value: "practiceCreate" },
          { label: "Practice Update", value: "practiceUpdate" },
          { label: "Practice Delete", value: "practiceDelete" },
          { label: "Practice Access All", value: "practiceAccessAll" },
        ],
      },
    ],
  },
  {
    groupName: "Assessment",
    subgroups: [
      {
        subgroupName: "Placement",
        rolePermissions: [
          { label: "Placement", value: "placement" },
          { label: "Placement Access All", value: "placementAccessAll" },
          { label: "Placement Create", value: "placementCreate" },
          { label: "Placement Update", value: "placementUpdate" },
          { label: "Placement Delete", value: "placementDelete" },
          { label: "Placement Result", value: "placementResult" },
          { label: "Placement Retry", value: "placementReassign" },
          { label: "Placement Recheck", value: "placementRecheck" },
          { label: "Placement Discard", value: "placementDiscard" },
          { label: "Placement Assign Public", value: "placementAssignPublic" },
          { label: "Placement Share Access", value: "placementShareAccess" },
        ],
      },
      {
        subgroupName: "Homework",
        rolePermissions: [
          { label: "Homework", value: "homework" },
          { label: "Homework Access All", value: "homeworkAccessAll" },
          { label: "Homework Create", value: "homeworkCreate" },
          { label: "Homework Update", value: "homeworkUpdate" },
          { label: "Homework Delete", value: "homeworkDelete" },
          { label: "Homework Result", value: "homeworkResult" },
          { label: "Homework Retry", value: "homeworkReassign" },
          { label: "Homework Recheck", value: "homeworkRecheck" },
          { label: "Homework Discard", value: "homeworkDiscard" },
          { label: "Homework Assign Public", value: "homeworkAssignPublic" },
          { label: "Homework Share Access", value: "homeworkShareAccess" },
        ],
      },
      {
        subgroupName: "Level Check",
        rolePermissions: [
          { label: "Level Check", value: "levelCheck" },
          { label: "Level Check Access All", value: "levelCheckAccessAll" },
          { label: "Level Check Create", value: "levelCheckCreate" },
          { label: "Level Check Update", value: "levelCheckUpdate" },
          { label: "Level Check Delete", value: "levelCheckDelete" },
          { label: "Level Check Result", value: "levelCheckResult" },
          { label: "Level Check Retry", value: "levelCheckReassign" },
          { label: "Level Check Recheck", value: "levelCheckRecheck" },
          { label: "Level Check Discard", value: "levelCheckDiscard" },
          { label: "Level Check Assign Public", value: "levelCheckAssignPublic" },
          { label: "Level Check Share Access", value: "levelCheckShareAccess" },
        ],
      },
      {
        subgroupName: "Exam",
        rolePermissions: [
          { label: "Exam", value: "exam" },
          { label: "Exam Access All", value: "examAccessAll" },
          { label: "Exam Create", value: "examCreate" },
          { label: "Exam Update", value: "examUpdate" },
          { label: "Exam Delete", value: "examDelete" },
          { label: "Exam Result", value: "examResult" },
          { label: "Exam Retry", value: "examReassign" },
          { label: "Exam Recheck", value: "examRecheck" },
          { label: "Exam Discard", value: "examDiscard" },
          { label: "Exam Assign Public", value: "examAssignPublic" },
          { label: "Exam Share Access", value: "examShareAccess" },
          { label: "Exam Result Status Update", value: "examResultStatusUpdate" },
        ],
      },
      {
        subgroupName: "Last Dance",
        rolePermissions: [
          { label: "Last Dance", value: "finalExam" },
          { label: "Last Dance Access All", value: "finalExamAccessAll" },
          { label: "Last Dance Create", value: "finalExamCreate" },
          { label: "Last Dance Update", value: "finalExamUpdate" },
          { label: "Last Dance Delete", value: "finalExamDelete" },
          { label: "Last Dance Result", value: "finalExamResult" },
          { label: "Last Dance Retry", value: "finalExamReassign" },
          { label: "Last Dance Recheck", value: "finalExamRecheck" },
          { label: "Last Dance Discard", value: "finalExamDiscard" },
          { label: "Last Dance Assign Public", value: "finalExamAssignPublic" },
          { label: "Last Dance Share Access", value: "finalExamShareAccess" },
        ],
      },
    ],
  },
  {
    groupName: "Folder",
    isNotNested: true,
    subgroups: [
      {
        subgroupName: "Overall",
        rolePermissions: [
          { label: "Folder", value: "folder" },
          { label: "Folder Create", value: "folderCreate" },
          { label: "Folder Update", value: "folderUpdate" },
          { label: "Folder Delete", value: "folderDelete" },
          { label: "Folder Access All", value: "folderAccessAll" },
        ],
      },
    ],
  },
  {
    groupName: "Short Link",
    isNotNested: true,
    subgroups: [
      {
        subgroupName: "Overall",
        rolePermissions: [
          { label: "Short Link", value: "shortLink" },
          { label: "Short Link Create", value: "shortLinkCreate" },
          { label: "Short Link Update", value: "shortLinkUpdate" },
          { label: "Short Link Delete", value: "shortLinkDelete" },
        ],
      },
    ],
  },
  {
    groupName: "Group Assessment",
    isNotNested: true,
    subgroups: [
      {
        subgroupName: "Overall",
        rolePermissions: [
          { label: "Group Assessment", value: "groupAssessment" },
          { label: "Group Assessment Stats", value: "groupAssessmentStats" },
          { label: "Group Assessment Create", value: "groupAssessmentCreate" },
          { label: "Group Assessment Update", value: "groupAssessmentUpdate" },
          { label: "Group Assessment Delete", value: "groupAssessmentDelete" },
        ],
      },
    ],
  },
  {
    groupName: "Competition",
    isNotNested: true,
    subgroups: [
      {
        subgroupName: "Overall",
        rolePermissions: [
          { label: "Competition", value: "competition" },
          { label: "Competition Create", value: "competitionCreate" },
          { label: "Competition Update", value: "competitionUpdate" },
          { label: "Competition Delete", value: "competitionDelete" },
          { label: "Competition Hard Sync", value: "competitionHardSync" },
          { label: "Competition Sync", value: "competitionSync" },
          { label: "Leaderboard Get", value: "competitionLeaderboardGet" },
          { label: "Competition Access All", value: "competitionAccessAll" },
        ],
      },
    ],
  },
  {
    groupName: "Lead",
    isNotNested: true,
    subgroups: [
      {
        subgroupName: "Leads",
        rolePermissions: [
          { label: "Lead", value: "lead" },
          { label: "Assessment Link", value: "assessmentLinkForLead" },
        ],
      },
    ],
  },
  {
    groupName: "User",
    isNotNested: true,
    subgroups: [
      {
        subgroupName: "Overall",
        rolePermissions: [
          { label: "User", value: "user" },
          { label: "User Create", value: "userCreate" },
          { label: "User Update", value: "userUpdate" },
          { label: "User Delete", value: "userDelete" },
          { label: "User Ban", value: "userBan" },
          { label: "User Unban", value: "userUnban" },
          { label: "User State", value: "userStateUpdate" },
          { label: "User Tariff", value: "userTariff" },
          { label: "User Extra Access", value: "userExtraAccess" },
          { label: "User Booking Limits", value: "userBookingLimits" },
          { label: "User Booking Unblock", value: "userBookingUnblock" },
          { label: "User AI Chat Toggle", value: "userAiChatToggle" },
          { label: "Referral", value: "referral" },
          { label: "Referral Status Update", value: "referralStatusUpdate" },
          { label: "Payout Request", value: "payoutRequest" },
          { label: "Payout Request Status Update", value: "payoutRequestStatusUpdate" },
        ],
      },
    ],
  },
  {
    groupName: "Group",
    isNotNested: true,
    subgroups: [
      {
        subgroupName: "Overall",
        rolePermissions: [
          { label: "Group", value: "group" },
          { label: "Group Create", value: "groupCreate" },
          { label: "Group Update", value: "groupUpdate" },
          { label: "Group Delete", value: "groupDelete" },
          { label: "Group Syllabus", value: "groupSyllabus" },
          { label: "Group Syllabus Assign", value: "groupSyllabusAssign" },
          { label: "Group Syllabus Delete", value: "groupSyllabusDelete" },
        ],
      },
    ],
  },
  {
    groupName: "Curriculums",
    subgroups: [
      {
        subgroupName: "Syllabus",
        rolePermissions: [
          { label: "Syllabus", value: "syllabus" },
          { label: "Syllabus Create", value: "syllabusCreate" },
          { label: "Syllabus Update", value: "syllabusUpdate" },
          { label: "Syllabus Delete", value: "syllabusDelete" },
        ],
      },
      {
        subgroupName: "Lesson",
        rolePermissions: [
          { label: "Lesson", value: "lesson" },
          { label: "Lesson Create", value: "lessonCreate" },
          { label: "Lesson Update", value: "lessonUpdate" },
          { label: "Lesson Delete", value: "lessonDelete" },
        ],
      },
      {
        subgroupName: "Country",
        rolePermissions: [
          { label: "Country", value: "country" },
          { label: "Country Create", value: "countryCreate" },
          { label: "Country Update", value: "countryUpdate" },
          { label: "Country Delete", value: "countryDelete" },
        ],
      },
      {
        subgroupName: "University",
        rolePermissions: [
          { label: "University", value: "university" },
          { label: "University Create", value: "universityCreate" },
          { label: "University Update", value: "universityUpdate" },
          { label: "University Delete", value: "universityDelete" },
        ],
      },
    ],
  },
  {
    groupName: "Bookings",
    subgroups: [
      {
        subgroupName: "Schedule",
        rolePermissions: [
          { label: "Schedule", value: "schedule" },
          { label: "Schedule Create", value: "scheduleCreate" },
          { label: "Schedule Update", value: "scheduleUpdate" },
          { label: "Schedule Delete", value: "scheduleDelete" },
        ],
      },
      {
        subgroupName: "Slot",
        rolePermissions: [
          { label: "Slot", value: "slot" },
          { label: "Slot Create", value: "slotCreate" },
          { label: "Slot Update", value: "slotUpdate" },
          { label: "Slot Delete", value: "slotDelete" },
          { label: "Slot Verify", value: "slotVerify" },
          { label: "Slot Access All", value: "slotAccessAll" },
          { label: "Slot Modify All", value: "slotModifyAll" },
          { label: "Slot Transfer", value: "slotTransfer" },
        ],
      },
      {
        subgroupName: "Support Teachers",
        rolePermissions: [{ label: "Statistics", value: "supportTeacherStats" }],
      },
    ],
  },
  {
    groupName: "Administration",
    subgroups: [
      {
        subgroupName: "Role",
        rolePermissions: [
          { label: "Role", value: "role" },
          { label: "Role Create", value: "roleCreate" },
          { label: "Role Update", value: "roleUpdate" },
          { label: "Role Delete", value: "roleDelete" },
        ],
      },
      {
        subgroupName: "Admin",
        rolePermissions: [
          { label: "Admin", value: "admin" },
          { label: "Admin Info", value: "adminInfo" },
          { label: "Admin Create", value: "adminCreate" },
          { label: "Admin Update", value: "adminUpdate" },
          { label: "Admin Delete", value: "adminDelete" },
        ],
      },
      {
        subgroupName: "Department",
        rolePermissions: [
          { label: "Department", value: "department" },
          { label: "Department Group", value: "departmentGroup" },
          { label: "Department Group Create", value: "departmentGroupCreate" },
          { label: "Department Group Update", value: "departmentGroupUpdate" },
          { label: "Department Group Delete", value: "departmentGroupDelete" },
          { label: "Department Create", value: "departmentCreate" },
          { label: "Department Update", value: "departmentUpdate" },
          { label: "Department Delete", value: "departmentDelete" },
        ],
      },
    ],
  },
  {
    groupName: "Trash",
    isNotNested: true,
    subgroups: [
      {
        subgroupName: "Overall",
        rolePermissions: [
          { label: "Trash", value: "trash" },
          { label: "Trash Restore", value: "trashRestore" },
          { label: "Trash Delete", value: "trashDelete" },
          { label: "Trash Access All", value: "trashAccessAll" },
        ],
      },
    ],
  },
  {
    groupName: "IT Staff",
    isNotNested: true,
    subgroups: [
      {
        subgroupName: "Overall",
        rolePermissions: [
          { label: "Assessment history update", value: "assessmentHistoryUpdate" },
          { label: "Assessment Section history update", value: "assessmentSectionHistoryUpdate" },
          { label: "Assessment Access History", value: "assessmentAccessHistory" },
          { label: "Platform Report", value: "platformReport" },
        ],
      },
    ],
  },
  {
    groupName: "Branch",
    isNotNested: true,
    subgroups: [
      {
        subgroupName: "Overall",
        rolePermissions: [
          { label: "Branch", value: "branch" },
          { label: "Branch Create", value: "branchCreate" },
          { label: "Branch Update", value: "branchUpdate" },
          { label: "Branch Delete", value: "branchDelete" },
        ],
      },
    ],
  },
];
